import { useAgentV4TeamSettings } from '@common/teams/useCopilotTeamSettings';
import { Entity } from '@common/types/Entity';
import { useIsAdminUser } from '@common/useUserType';
import { UpdateCopilotEnabledMutationQuery } from '@pages/UserSettings/queries';
import { theme } from '@prophecy/theme';
import { Stack, StackItem } from '@prophecy/ui-v3/Layout';
import { toast } from '@prophecy/ui-v3/LegacyToast';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { Switch } from '@prophecy/ui-v3/Switch';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useGraphQlMutation } from 'frontend/core/src/data/util';
import { useState } from 'react';

export default function CopilotSettings({ teamId, copilotEnabled }: { teamId: string; copilotEnabled: boolean }) {
  const queryClient = useQueryClient();
  const { isAdmin } = useIsAdminUser();

  const { agentV4Enabled, isUpdatingAgentV4Enabled, updateAgentV4Enabled } = useAgentV4TeamSettings(teamId);

  const { isClusterLevelCopilotEnabled: isGlobalCopilotEnabled, isAgentV4EnabledAtClusterLevel } = useGlobalVariables();
  const [isCopilotEnabled, setIsCopilotEnabled] = useState(copilotEnabled);
  const { mutateAsync: updateCopilotEnabled, isLoading: isUpdatingCopilotEnabled } = useGraphQlMutation(
    UpdateCopilotEnabledMutationQuery,
    {
      onError: () => {
        toast.error({ content: 'Failed to update copilot team flag!' });
      }
    }
  );

  const disabledMessageCopilot = !isAdmin
    ? 'You are not authorized to update copilot settings.'
    : !isGlobalCopilotEnabled
      ? 'Copilot is disabled for this cluster'
      : isUpdatingCopilotEnabled
        ? 'Updating copilot settings...'
        : undefined;

  return (
    <Stack direction='horizontal' gap={theme.spaces.x16}>
      <StackItem grow='1'>
        <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
          <Switch
            checked={isCopilotEnabled && isGlobalCopilotEnabled}
            disabled={Boolean(disabledMessageCopilot)}
            tooltip={disabledMessageCopilot}
            data-test-id='copilot-enable-switch'
            onChange={async (enabled) => {
              setIsCopilotEnabled(enabled);
              try {
                await updateCopilotEnabled({ teamUID: teamId, enabled });
                queryClient.invalidateQueries([Entity.Team, 'allTeams']);
              } catch {
                setIsCopilotEnabled(!enabled);
              }
            }}
            helpText='Enable copilot features for the team'>
            Enable Copilot
          </Switch>
          {isUpdatingCopilotEnabled ? <Spinner spinning size='s' /> : <Stack width='20px' />}
        </Stack>
      </StackItem>
      <StackItem grow='1'>
        <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
          <Switch
            tooltip={
              !isAgentV4EnabledAtClusterLevel ? 'Conversational agents are disabled for this cluster' : undefined
            }
            data-test-id='copilot-agent-v4-switch'
            disabled={isUpdatingAgentV4Enabled || !isAgentV4EnabledAtClusterLevel}
            onChange={updateAgentV4Enabled}
            checked={isAgentV4EnabledAtClusterLevel && agentV4Enabled}
            helpText={'Enable conversational agents for the team'}>
            Enable Conversational Agents
          </Switch>
          {isUpdatingAgentV4Enabled ? <Spinner spinning size='s' /> : <Stack width='20px' />}
        </Stack>
      </StackItem>
    </Stack>
  );
}
